import {ChangeDetectionStrategy, Component, computed, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {combineLatest, Observable, Subscription, zip} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';

import {ViewBreakpointService, ViewSize} from '@common-module/app-services/view-breakpoint.service';
import {FooterButtonsService} from '../shared/services/footer-buttons.service';
import {UiSpinnerService} from '@common-module/app-services/ui-spinner.service';
import {SelectedService} from '../shared/services/selected.service';
import {UnitGroupsService} from '../shared/services/unit-groups.service';
import {UnitStateService} from '../shared/services/unit-state.service';
import {UnitFilterService} from '../shared/services/unit-filter.service';
import {ChartDataService} from '../shared/services/chart-data.service';
import {TrackLayerService} from '../shared/services/track-layer.service';
import {NotificationManagerService} from '../shared/services/notification-manager.service';
import {TileLayerService} from '../shared/services/tile-layer.service';
import {GeofenceListService} from '../shared/services/geofence-list.service';
import {MapToolsService} from '../shared/services/map-tools.service';
import {MapSelectUnitService} from '../shared/services/map-select-unit.service';
import {ToolbarService} from "../shared/services/toolbar.service";
import {isPublicView} from "../app.module";
import {ToggleSidebarService} from "../shared/services/toggle-sidebar.service";
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from "@angular/material/sidenav";
import {PermissionsSecurityService} from "../shared/services/permissions-security.service";
import {AppMessageService} from "@common-module/app-services/app-message.service";
import { HeaderComponent } from './header/header.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { SideNavComponent } from './side-nav/side-nav.component';
import { MatIcon } from '@angular/material/icon';
import { FooterComponent } from './footer/footer.component';
import {isRouteWithFooter, isRouteWithSideNav} from './system-sidebar-routes';

@Component({
    selector: 'app-system',
    templateUrl: './system.component.html',
    styleUrls: ['./system.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HeaderComponent, MatSidenavContainer, NgClass, MatSidenav, SideNavComponent, MatSidenavContent, MatIcon, RouterOutlet, FooterComponent, AsyncPipe]
})

export class SystemComponent implements OnInit, OnDestroy {

  public isPublicInterface = false;
  public opened = true;
  public loadingComplete = false;
  public size$: Observable<ViewSize>;
  public isSideNavAvailable: boolean = true;
  public isFooterAvailable = true;

  private subscription: Subscription;
  private routesSubscription: Subscription;

  @ViewChild('sidenav') public sidenav: MatSidenav;
  private touchStartX = 0;



  constructor(private chartDataService: ChartDataService,
              private footerButtonsService: FooterButtonsService,
              private geofenceListService: GeofenceListService,
              private mapSelectUnitService: MapSelectUnitService,
              private mapToolsService: MapToolsService,
              private notificationManagerService: NotificationManagerService,
              private router: Router,
              private selectedService: SelectedService,
              private tileLayerService: TileLayerService,
              private trackLayerService: TrackLayerService,
              private uiSpinnerService: UiSpinnerService,
              private unitGroupsService: UnitGroupsService,
              private unitStateService: UnitStateService,
              private unitFilterService: UnitFilterService,
              private viewBreakpointService: ViewBreakpointService,
              private toogleSidebarService: ToggleSidebarService,
              private toolbarService: ToolbarService,
              private appMessageService: AppMessageService,
              private readonly permissionsSecurityService: PermissionsSecurityService) {
  }

  public isPermissionsReady = computed(() => {
      return this.permissionsSecurityService.isPermissionsReady()
    }
  )

  ngOnInit(): void {
    this.size$ = this.viewBreakpointService.size$;
    if (isPublicView()) {
      this.loadPublicInterface();
    } else {
      this.loadUsualInterface();
    }
    this.isPublicInterface = this.router.url.includes("public");
    this.chekSidenavStatus();
  }

  public onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  public onTouchEnd(event: TouchEvent): void {
    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    if (deltaX < -50 && this.opened) {
      this.sidenav.close();
      this.footerButtonsService.changeIsOpenSideNav(false);
    }
  }

  private loadPublicInterface(): void {
    this.loadFooterButtons();
    this.uiSpinnerService.show();
    this.unitStateService.init();
    this.unitGroupsService.initWithOutGroups();
    this.unitFilterService.init(true);
    this.tileLayerService.init();
    this.subscription = zip(
      this.unitStateService.unitsStateWithCheck$,
      this.unitGroupsService.allUnits$
    )
      .pipe(
        map(([unitsStateWithCheck, unitList]) =>
          ({unitsStateWithCheck: unitsStateWithCheck, unitList: unitList}))
      )
      .subscribe((res) => {
          if (res.unitsStateWithCheck?.unitsState && res.unitList) {
            this.uiSpinnerService.stop();
            this.loadingComplete = true;
            this.subscription.unsubscribe();
          }
        },
        error => {
          this.appMessageService.openSnackBar('Ошибка при загрузке приложения. Попробуйте перезагрузить страницу')
          if (this.subscription) {
            this.subscription.unsubscribe();
          }
          throw error;
        });
  }

  private loadUsualInterface(): void {

    this.loadFooterButtons();
    this.toolbarService.init();
    this.uiSpinnerService.show();
    this.unitGroupsService.init();
    this.unitStateService.init();
    this.unitFilterService.init();
    this.notificationManagerService.init();
    this.tileLayerService.init();

    this.subscription = combineLatest(
      [
        this.unitStateService.unitsStateWithCheck$,
        this.unitGroupsService.unitGroups$]
    )
      .pipe(
        map(([unitsStateWithCheck, unitGroups]) =>
          ({unitsStateWithCheck: unitsStateWithCheck, unitGroups: unitGroups}))
      )
      .subscribe((res) => {
          if (res.unitGroups && res.unitsStateWithCheck?.unitsState) {
            this.uiSpinnerService.stop();
            this.loadingComplete = true;
            if (this.subscription) {
              this.subscription.unsubscribe();
            }
          }
        },
        error => {
          this.appMessageService.openSnackBar('Ошибка при загрузке приложения. Попробуйте перезагрузить страницу')
          if (this.subscription) {
            this.subscription.unsubscribe();
          }
          throw error;
        });

  }

  private loadFooterButtons(): void {
    this.footerButtonsService.currentIsOpenSideNav$.subscribe(f => {
      this.opened = f;
    });
    this.viewBreakpointService.size$
      .pipe(take(2))
      .subscribe(size => {
        if (size.less('l')) {
          this.opened = false;
          this.footerButtonsService.changeIsOpenSideNav(false);
        }
      });
  }

  private chekSidenavStatus(): void {
    this.isSideNavAvailable = this.isRouteWithSideNav(this.router.url);
    this.isFooterAvailable = isRouteWithFooter(this.router.url);
    this.opened = this.isSideNavAvailable;
    this.routesSubscription = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap((event) => {
          this.isSideNavAvailable = this.isRouteWithSideNav(event.url);
          this.isFooterAvailable = isRouteWithFooter(event.urlAfterRedirects);
          this.opened = this.isSideNavAvailable;
        })
      )
      .subscribe()
  }

  private isRouteWithSideNav(url: string): boolean {
    return isRouteWithSideNav(url);
  }

  public updateMap(): void {
    setTimeout(() => {
      if (this.mapSelectUnitService.map) {
        this.mapSelectUnitService.map.updateSize();
      }
    }, 500);
    this.toogleSidebarService.nextToggle();
  }

  ngOnDestroy(): void {
    this.chartDataService.destroy();
    this.geofenceListService.destroy();
    this.mapToolsService.destroy();
    this.notificationManagerService.destroy();
    this.unitFilterService.destroy();
    this.unitGroupsService.destroy();
    this.unitStateService.destroy();
    this.selectedService.destroy();
    this.trackLayerService.destroy();
    this.uiSpinnerService.stop();
    this.routesSubscription.unsubscribe();
  }

  protected readonly isPublicView = isPublicView;
}
